export interface PopoverPlacement {
  left: number;
  top: number;
  /** `above` when there was not room below — useful for a caret/animation direction. */
  side: 'below' | 'above';
}

/**
 * 096: where a portalled popover goes, given its anchor, its own size and the viewport.
 *
 * 🔴 Extracted as a pure function because the defect it fixes was found in a BROWSER, not in a
 * test. The first implementation always placed the popover at `anchor.bottom + gap`; dragging the
 * bottom-most lane of a scrolled grid then put it at `top=810` with a height of 263 in a 900px
 * viewport, leaving 173px — including the Create button — below the fold and unreachable.
 *
 * jsdom has no layout, so the real geometry can only ever be checked in a browser (quickstart
 * L11). What CAN be checked here is the arithmetic, which is where the bug actually lived.
 *
 * 115: relocated here from `pages/schedule/timeline/geometry.ts` — this function has no
 * schedule-specific coupling, and the sidebar footer's `Popover.tsx` is its second consumer.
 */
export function placePopover(
  anchor: { top: number; bottom: number; left: number },
  size: { width: number; height: number },
  viewport: { width: number; height: number },
  gap = 8,
): PopoverPlacement {
  const fitsBelow = anchor.bottom + gap + size.height <= viewport.height;
  // Flip above only if that actually helps — with no room either way, below-and-clamped at least
  // keeps the top edge (and the heading) visible.
  const roomAbove = anchor.top - gap - size.height >= 0;
  const side: 'below' | 'above' = fitsBelow || !roomAbove ? 'below' : 'above';

  const rawTop = side === 'below' ? anchor.bottom + gap : anchor.top - gap - size.height;
  const top = Math.max(gap, Math.min(rawTop, viewport.height - size.height - gap));
  const left = Math.max(gap, Math.min(anchor.left, viewport.width - size.width - gap));

  return { left, top, side };
}
